<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import ReportsAPI from 'dashboard/api/reports';
import ReportHeader from './components/ReportHeader.vue';
import OverviewReportFilters from './components/OverviewReportFilters.vue';
import CustomAttributeDrilldownDrawer from './components/CustomAttributeDrilldownDrawer.vue';
import BarChart from 'shared/components/charts/BarChart.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const PALETTE = [
  '#4A9E8F',
  '#6C5CE7',
  '#E17055',
  '#0984E3',
  '#D63384',
  '#F0B429',
  '#20C997',
  '#845EF7',
  '#FA5252',
  '#15AABF',
];

const { t } = useI18n();
const getters = useStoreGetters();
const store = useStore();

const listAttributes = computed(() =>
  getters['attributes/getConversationAttributes'].value.filter(
    attribute => attribute.attributeDisplayType === 'list'
  )
);

const selectedAttributeKey = ref('');
const dateRange = ref({ from: null, to: null });
const isLoading = ref(false);
const report = ref(null);
const hasFetchError = ref(false);

const drilldown = ref({
  open: false,
  title: '',
  attributeKey: '',
  value: '',
  parentAttributeKey: '',
  parentValue: '',
});

const canFetch = computed(
  () => !!selectedAttributeKey.value && !!dateRange.value.from
);

const fetchReport = async () => {
  if (!canFetch.value) return;

  isLoading.value = true;
  hasFetchError.value = false;
  try {
    const response = await ReportsAPI.getCustomAttributeAnalytics({
      attributeKey: selectedAttributeKey.value,
      from: dateRange.value.from,
      to: dateRange.value.to,
    });
    report.value = response.data;
  } catch (error) {
    hasFetchError.value = true;
  } finally {
    isLoading.value = false;
  }
};

const onFilterChange = ({ from, to }) => {
  dateRange.value = { from, to };
};

watch(selectedAttributeKey, fetchReport);
watch(dateRange, fetchReport, { deep: true });

onMounted(() => {
  store.dispatch('attributes/get', 0);
});

watch(
  listAttributes,
  attributes => {
    if (!selectedAttributeKey.value && attributes.length) {
      selectedAttributeKey.value = attributes[0].attributeKey;
    }
  },
  { immediate: true }
);

const buildChartData = counts => {
  const entries = Object.entries(counts);
  return {
    labels: entries.map(([label]) => label),
    datasets: [
      {
        data: entries.map(([, count]) => count),
        backgroundColor: entries.map(
          (_, index) => PALETTE[index % PALETTE.length]
        ),
      },
    ],
  };
};

const buildChartOptions = counts => {
  const total = Object.values(counts).reduce((sum, count) => sum + count, 0);
  return {
    plugins: {
      tooltip: {
        callbacks: {
          label: context => {
            const value = context.parsed.y;
            const percentage = total
              ? ((value / total) * 100).toFixed(1)
              : '0.0';
            return `${context.label}: ${value} conversations (${percentage}%)`;
          },
        },
      },
    },
  };
};

const parentChartData = computed(() =>
  report.value ? buildChartData(report.value.parent_distribution) : null
);
const parentChartOptions = computed(() =>
  report.value ? buildChartOptions(report.value.parent_distribution) : {}
);

const subDistributionEntries = computed(() =>
  report.value ? Object.entries(report.value.sub_distributions) : []
);

const openDrilldown = state => {
  drilldown.value = { open: true, ...state };
};

const closeDrilldown = () => {
  drilldown.value = { ...drilldown.value, open: false };
};

const onParentSegmentClick = ({ label }) => {
  openDrilldown({
    title: `${report.value.attribute_display_name}: ${label}`,
    attributeKey: report.value.attribute_key,
    value: label,
    parentAttributeKey: '',
    parentValue: '',
  });
};

const onSubSegmentClick =
  (parentValue, subAttribute) =>
  ({ label }) => {
    openDrilldown({
      title: t('CUSTOM_ATTRIBUTE_ANALYTICS_REPORTS.SUB_DISTRIBUTION_TITLE', {
        parentValue,
        attributeName: label,
      }),
      attributeKey: subAttribute.attribute_key,
      value: label,
      parentAttributeKey: report.value.attribute_key,
      parentValue,
    });
  };

const onDownloadClick = () => {
  store.dispatch('downloadCustomAttributeAnalyticsReport', {
    attributeKey: selectedAttributeKey.value,
    from: dateRange.value.from,
    to: dateRange.value.to,
  });
};
</script>

<template>
  <ReportHeader
    :header-title="$t('CUSTOM_ATTRIBUTE_ANALYTICS_REPORTS.HEADER')"
    :header-description="$t('CUSTOM_ATTRIBUTE_ANALYTICS_REPORTS.DESCRIPTION')"
  >
    <NextButton
      :label="$t('CUSTOM_ATTRIBUTE_ANALYTICS_REPORTS.DOWNLOAD_REPORT')"
      icon="i-ph-download-simple"
      size="sm"
      :disabled="!canFetch"
      @click="onDownloadClick"
    />
  </ReportHeader>

  <div v-if="!listAttributes.length" class="py-10 text-center text-n-slate-11">
    {{ $t('CUSTOM_ATTRIBUTE_ANALYTICS_REPORTS.NO_LIST_ATTRIBUTES') }}
  </div>

  <template v-else>
    <div class="flex flex-col gap-4">
      <label class="max-w-xs">
        <span class="text-sm font-medium text-n-slate-12">
          {{ $t('CUSTOM_ATTRIBUTE_ANALYTICS_REPORTS.ATTRIBUTE_PICKER_LABEL') }}
        </span>
        <select v-model="selectedAttributeKey">
          <option
            v-for="attribute in listAttributes"
            :key="attribute.attributeKey"
            :value="attribute.attributeKey"
          >
            {{ attribute.attributeDisplayName }}
          </option>
        </select>
      </label>

      <OverviewReportFilters @filter-change="onFilterChange" />
    </div>

    <div v-if="isLoading" class="flex h-64 items-center justify-center">
      <Spinner />
    </div>

    <div
      v-else-if="hasFetchError || !report"
      class="py-10 text-center text-n-slate-11"
    >
      {{ $t('CUSTOM_ATTRIBUTE_ANALYTICS_REPORTS.NO_ENOUGH_DATA') }}
    </div>

    <div
      v-else
      class="grid grid-cols-1 gap-6 py-6 md:grid-cols-2 xl:grid-cols-3"
    >
      <div class="rounded-xl border border-n-weak p-4">
        <h4 class="mb-3 text-n-slate-12">
          {{ report.attribute_display_name }}
        </h4>
        <div class="h-64">
          <BarChart
            :collection="parentChartData"
            :chart-options="parentChartOptions"
            clickable
            @element-click="onParentSegmentClick"
          />
        </div>
      </div>

      <div
        v-for="[parentValue, subAttribute] in subDistributionEntries"
        :key="parentValue"
        class="rounded-xl border border-n-weak p-4"
      >
        <h4 class="mb-3 text-n-slate-12">
          {{ parentValue }}
        </h4>
        <div class="h-64">
          <BarChart
            :collection="buildChartData(subAttribute.counts)"
            :chart-options="buildChartOptions(subAttribute.counts)"
            clickable
            @element-click="onSubSegmentClick(parentValue, subAttribute)"
          />
        </div>
      </div>
    </div>
  </template>

  <CustomAttributeDrilldownDrawer
    :open="drilldown.open"
    :title="drilldown.title"
    :attribute-key="drilldown.attributeKey"
    :value="drilldown.value"
    :parent-attribute-key="drilldown.parentAttributeKey"
    :parent-value="drilldown.parentValue"
    :from="dateRange.from"
    :to="dateRange.to"
    @close="closeDrilldown"
  />
</template>
